// High Valyrian text normalisation.
//
// Storage convention (see CLAUDE.md): NFC, precomposed macron vowels ā ē ī ō ū ȳ, lowercase
// except proper nouns, ASCII apostrophe. `normalizeHv()` is the one entry point for any
// user input (typing cards, dictionary search box, translator, own words, letters).
//
// ASCII / keyboard fallbacks accepted by `normalizeHv()` (decided 2026-10):
//   - combining macron U+0304 after a vowel → precomposed (via NFC), e.g. "ā" → "ā"
//   - vowel + colon            "a:" → "ā"   (colon never occurs inside an HV word)
//   - circumflex               "â" → "ā", "ŷ" → "ȳ" (common "long vowel" fallback on phones)
//   - macron-like modifiers    "a¯", "aˉ" → "ā"
//   - doubled vowels           "aa" → "ā"  ONLY with `{ doubledVowels: true }`, because
//                              non-HV names on the wiki (Braavos, Daario, Meereenese) contain them.
//                              Typing cards may enable it; search and storage do not.
//   - typographic apostrophes  ’ ‘ ʼ ` ´ → ' (ASCII)
// Acute vowels (á é í ó ú ý) are kept: the wiki's {{hv-IPA}} uses them to mark irregular stress.

export const MACRON_VOWELS = ['ā', 'ē', 'ī', 'ō', 'ū', 'ȳ'] as const;
export const MACRON_VOWELS_UPPER = ['Ā', 'Ē', 'Ī', 'Ō', 'Ū', 'Ȳ'] as const;

/** Keys for the on-screen macron row (DESIGN.md "Typing cards"): lowercase, then Shift capitals. */
export const MACRON_KEYS: readonly { key: string; shift: string; base: string }[] = [
  { key: 'ā', shift: 'Ā', base: 'a' },
  { key: 'ē', shift: 'Ē', base: 'e' },
  { key: 'ī', shift: 'Ī', base: 'i' },
  { key: 'ō', shift: 'Ō', base: 'o' },
  { key: 'ū', shift: 'Ū', base: 'u' },
  { key: 'ȳ', shift: 'Ȳ', base: 'y' },
];

/**
 * The High Valyrian alphabet in the wiki's dictionary order (Peterson's romanisation).
 * Digraphs are single letters: gh, kh, lj, mh, rh, th. (ñ is a letter of its own.)
 */
export const HV_LETTERS: readonly string[] = [
  'a',
  'ā',
  'b',
  'd',
  'e',
  'ē',
  'g',
  'gh',
  'h',
  'i',
  'ī',
  'j',
  'k',
  'kh',
  'l',
  'lj',
  'm',
  'mh',
  'n',
  'ñ',
  'o',
  'ō',
  'p',
  'q',
  'r',
  'rh',
  's',
  't',
  'th',
  'u',
  'ū',
  'v',
  'y',
  'ȳ',
  'z',
];

const SHORT_TO_LONG: Record<string, string> = {
  a: 'ā',
  e: 'ē',
  i: 'ī',
  o: 'ō',
  u: 'ū',
  y: 'ȳ',
  A: 'Ā',
  E: 'Ē',
  I: 'Ī',
  O: 'Ō',
  U: 'Ū',
  Y: 'Ȳ',
};
const LONG_TO_SHORT: Record<string, string> = {
  ā: 'a',
  ē: 'e',
  ī: 'i',
  ō: 'o',
  ū: 'u',
  ȳ: 'y',
  Ā: 'A',
  Ē: 'E',
  Ī: 'I',
  Ō: 'O',
  Ū: 'U',
  Ȳ: 'Y',
};
const CIRCUMFLEX: Record<string, string> = {
  â: 'ā',
  ê: 'ē',
  î: 'ī',
  ô: 'ō',
  û: 'ū',
  ŷ: 'ȳ',
  Â: 'Ā',
  Ê: 'Ē',
  Î: 'Ī',
  Ô: 'Ō',
  Û: 'Ū',
  Ŷ: 'Ȳ',
};
const ACUTE_TO_PLAIN: Record<string, string> = {
  á: 'a',
  é: 'e',
  í: 'i',
  ó: 'o',
  ú: 'u',
  ý: 'y',
  Á: 'A',
  É: 'E',
  Í: 'I',
  Ó: 'O',
  Ú: 'U',
  Ý: 'Y',
};

export interface NormalizeOptions {
  /** Keep letter case (proper nouns, display headwords). Default false: lowercase everything. */
  keepCase?: boolean;
  /** Also read doubled vowels "aa" as long "ā". Default false (see header comment). */
  doubledVowels?: boolean;
}

// Fast paths (performance, docs/audit/performance.md "Pages"): most calls get text already in the
// storage form (lowercase, precomposed, single spaces) — the form index folds ~300k of them, a page
// normalises every word it shows. Such text comes back unchanged from the full path, so it is
// returned as it is. ALREADY_LOWER: only lowercase letters, digits and punctuation that no rule
// below touches (no ’ ‘ ` ´ ′ ʼ, no ¯ ˉ, no colon, no circumflex, no uppercase, no combining
// marks; every character here is NFC on its own and none composes with a neighbour), words separated
// by single spaces, no space at either end. Not with `doubledVowels` (aa → ā would change it).
const ALREADY_LOWER = /^[a-z0-9āēīōūȳáéíóúýñ'.,!?;"()-]+(?: [a-z0-9āēīōūȳáéíóúýñ'.,!?;"()-]+)*$/;
/** Characters whose NFC form is themselves alone and that no NFC composition joins (ASCII, Latin-1, Latin Extended-A/B). */
const PLAIN_NFC = /^[\x20-\x7e -ɏ]*$/;

/** Normalise any HV input to the storage convention. Collapses runs of whitespace and trims. */
export function normalizeHv(s: string, opts: NormalizeOptions = {}): string {
  if (!opts.doubledVowels && ALREADY_LOWER.test(s)) return s;
  let t = s.normalize('NFC');
  t = t.replace(/[’‘ʼ`´′]/g, "'");
  // macron-like spacing modifiers typed after a vowel
  t = t.replace(/([aeiouyAEIOUY])[¯ˉ]/g, (_, v: string) => SHORT_TO_LONG[v] ?? v);
  // vowel + colon (but not a colon used as punctuation after a word: "a: " at a word end is still a long vowel by this rule;
  // HV has no colon-separated words, so we accept it)
  t = t.replace(/([aeiouyAEIOUY]):/g, (_, v: string) => SHORT_TO_LONG[v] ?? v);
  t = t.replace(/[âêîôûŷÂÊÎÔÛŶ]/g, (c) => CIRCUMFLEX[c] ?? c);
  if (opts.doubledVowels) {
    t = t.replace(/([aeiouyAEIOUY])\1/g, (_, v: string) => SHORT_TO_LONG[v] ?? v);
  }
  if (!opts.keepCase) t = t.toLowerCase();
  t = t.replace(/\s+/g, ' ').trim();
  return t.normalize('NFC');
}

/** ā→a, ē→e, … ȳ→y (keeps case and everything else). */
export function stripMacrons(s: string): string {
  return (PLAIN_NFC.test(s) ? s : s.normalize('NFC')).replace(/[āēīōūȳĀĒĪŌŪȲ]/g, (c) => LONG_TO_SHORT[c] ?? c);
}

/**
 * Lenient key for search and duplicate detection: normalised, lowercase, macrons and acute
 * accents removed, ñ → n, apostrophes and hyphens removed. Never display or store this.
 */
export function foldForSearch(s: string): string {
  // text already in the storage form (the form index's ~300k forms): one pass of the same mappings
  if (ALREADY_LOWER.test(s)) return s.replace(/[āēīōūȳáéíóúýñ'-]/g, (c) => FOLD_LOWER[c]!);
  return stripMacrons(normalizeHv(s))
    .replace(/[áéíóúý]/g, (c) => ACUTE_TO_PLAIN[c] ?? c)
    .replace(/ñ/g, 'n')
    .replace(/['-]/g, '');
}

/** foldForSearch's mappings for lowercase storage-form text, in one table (each maps one character to at most one, never to another key). */
const FOLD_LOWER: Record<string, string> = {
  ā: 'a',
  ē: 'e',
  ī: 'i',
  ō: 'o',
  ū: 'u',
  ȳ: 'y',
  á: 'a',
  é: 'e',
  í: 'i',
  ó: 'o',
  ú: 'u',
  ý: 'y',
  ñ: 'n',
  "'": '',
  '-': '',
};

const HV_WORD = /^-?[abdeghijklmnopqrstuvyzāēīōūȳñ]+(?:['][abdeghijklmnopqrstuvyzāēīōūȳñ]+)*-?$/;

/**
 * True when `s` is a single word written only with High Valyrian letters (case-insensitive),
 * optionally with a leading or trailing hyphen (affixes). c, f, w, x and doubled vowels never
 * occur in native HV words; doubled vowels are rejected.
 */
export function isHvWord(s: string): boolean {
  const t = s.normalize('NFC').toLowerCase();
  if (!HV_WORD.test(t)) return false;
  if (/(aa|ee|ii|oo|uu|yy)/.test(t)) return false;
  return /[aeiouyāēīōūȳ]/.test(t);
}

/** Split HV text into HV-letter units (digraphs as one unit), e.g. "rhaenyra" → rh a e n y r a. */
export function hvLetters(word: string): string[] {
  const out: string[] = [];
  const w = word.normalize('NFC');
  for (let i = 0; i < w.length;) {
    const two = w.slice(i, i + 2).toLowerCase();
    if (['gh', 'kh', 'lj', 'mh', 'rh', 'th'].includes(two)) {
      out.push(w.slice(i, i + 2));
      i += 2;
    } else {
      const cp = w.codePointAt(i)!;
      const ch = String.fromCodePoint(cp);
      out.push(ch);
      i += ch.length;
    }
  }
  return out;
}
